import React from 'react';
import {observer} from "mobx-react";
import {MenuItemComponentProps} from "../../services/menu-items-common/menu-item-component-props.interface";
import {ComponentServicesPick} from "../../services/component-services-pick";
import {withServices} from "../../react-hooks/with-services";
import classNames from 'classnames';
import {HomepageHeaderArenaBadgeBox} from './boxes/home-page-header-arena-badge/home-page-header-arena-badge.box';
import {HomepageHeaderArenaMenuItemBox} from './boxes/menu/menu-item/home-page-header-arena-menu-item.box';
import {Link} from '../raw-components/link/link';
import {CyClass} from '../../../common/cy-classes';
import {HomepageHeaderArenaMenuItemIconBox} from './boxes/menu/icon-box/home-page-header-arena-menu-item-icon.box';
import {HomepageHeaderArenaMenuItemNameBox} from './boxes/menu/menu-item-name/home-page-header-arena-menu-item-name.box';
import {HomepageHeaderArenaTileBox} from './boxes/tiles/tile/home-page-header-arena-tile.box';
import {HomePageHeaderArenaTileComponent} from './home-page-header-arena-tile.component';
import {HomepageHeaderArenaNameBox} from './boxes/tiles/name/home-page-header-arena-name.box';
import {HomepageHeaderArenaLinkBox} from './boxes/home-page-header-arena-link/home-page-header-arena-link.box';
import {SeoConfiguration} from '../../services/configurations/config-data-sections/seo.configuration';
import {ISendElasticEventOptions} from '../../services/analytics/analytics.service.interface';

interface HomePageHeaderArenaMenuItemComponentProps extends MenuItemComponentProps, ComponentServicesPick<'homepage' | 'location' | 'mediaQuery' | 'device' | 'theme' | 'configuration' | 'translation' | 'analytics'> {
    itemIndex: number;
    totalItemsCount: number;
}
const ComponentBody: React.FC<HomePageHeaderArenaMenuItemComponentProps> = observer((props) => {
    const menuItem = props.menuItem;

    const menuButtonIconMap = new Map([
        ["liveCasinoMenuItem", props.theme.currentTheme.icons.sideMenuLiveCasino],
        ["slotsMenuItem", props.theme.currentTheme.icons.sideMenuSlots],
        ["newGamesMenuItem", props.theme.currentTheme.icons.sideMenuNewGames],
        ["jackpotsMenuItem", props.theme.currentTheme.icons.sideMenuJackpots],
        ["exclusive888arenaMenuItem", props.theme.currentTheme.icons.sideMenuExclusive888],
        ["scratchCardsMenuItem", props.theme.currentTheme.icons.sideMenuScratchCards],
        ["casinoMenuItem", props.theme.currentTheme.icons.sideMenuCasinoGames]
    ]);
    function _getButtonIcon(label: string) {
        const iconKey = label.trim().replace(/(-|\s)/g, "_");
        return menuButtonIconMap.has(iconKey) ? menuButtonIconMap.get(iconKey)! : props.theme.currentTheme.icons.sideMenuExclusive888;
    }
    function _getSeo(prop: keyof SeoConfiguration["categories"]["images"], placeholder: string, dynamic: string) {

        let seoValue = '';
        let seoConfig = props.configuration.data.seo;
    
        if (seoConfig && seoConfig.categories && seoConfig.categories.images && seoConfig.categories.images[prop]) {
          seoValue = seoConfig.categories.images[prop];
        }
    
        return props.translation.translate(seoValue, {
          [placeholder]: dynamic
        });
    }

    const handleLinkClick = async ()  => {
        let data: ISendElasticEventOptions = {
            eventType: "Orbit_AI_Feature",
            category: 'Game Grid',
            action: 'Home',
            label: `${props.menuItem.key}_${props.itemIndex}`
        }      
        props.analytics.sendElasticInfoEvent(data, true);

        await props.menuItem.execute();
    }

    let href = props.menuItem.getLinkUrl() ?? "";
    if(href) {
        href = props.location.buildFullUri(href).toString();
    }

    const hasNewGamesIndication = menuItem.key === "newGamesMenuItem" && props.homepage.newAddedGames > 0;
    const newGamesIndication = hasNewGamesIndication ?
        <HomepageHeaderArenaBadgeBox className={CyClass('cy-new-games-arena-badge')} headerArenasStyleIsMenu={props.homepage.headerArenasStyleIsMenu}>
            {props.homepage.newAddedGames > 10 ? '10+' : props.homepage.newAddedGames}
        </HomepageHeaderArenaBadgeBox>
        : null;

    if (props.homepage.headerArenasStyleIsMenu) {
        const Icon = _getButtonIcon(menuItem.key);
        return <Link href={href} enableTap={props.device.isMobile} key={menuItem.key}>
            <HomepageHeaderArenaMenuItemBox
                className={classNames(menuItem.key, CyClass('cy-arena-box'))}
                onClick={handleLinkClick}
            >
                <HomepageHeaderArenaMenuItemIconBox>
                    <Icon size={props.mediaQuery.xlAndAbove ? 3.6 : 2} />
                    {newGamesIndication}
                </HomepageHeaderArenaMenuItemIconBox>
                <HomepageHeaderArenaMenuItemNameBox>
                    <span className={CyClass('cy-header-arena-title')}>
                        { props.menuItem.text }
                    </span>
                </HomepageHeaderArenaMenuItemNameBox>
            </HomepageHeaderArenaMenuItemBox>
        </Link>
    }

    return (
        <HomepageHeaderArenaTileBox
            key={menuItem.key}
            className={classNames(menuItem.key, CyClass('cy-arena-box'))}
            onClick={handleLinkClick}
            useSmallHeaderArenas={props.homepage.headerArenasStyleIsSmallTiles}
            totalItemsCount={props.totalItemsCount}
        >
            {/* We need this href only for SEO*/}
            <Link href={href} enableTap={props.device.isMobile} box={HomepageHeaderArenaLinkBox}>
            <HomePageHeaderArenaTileComponent
                // OBSERVATION: I'm using here menuItem.icon as is, directly in src attribute because it's a path to an image.
                // If something more complex is needed, see implementation from generic side menu item where a check for menuItem.iconSource is made and
                // and also get (from lodash) is used to be able to set in config just the relative image path and not the whole thing
                src={menuItem.icon}
                enableTap={props.device.isMobile}
                lazy={true}
                className={'arena-img'}
                fallbackSrc={props.configuration.data.generalSettings.gamesSettings.homePageArenaDefaultPlaceHolder}
                alt={_getSeo('alt', 'categoryName', menuItem.text)}
                title={_getSeo('title', 'categoryName', menuItem.text)}
            />
            <HomepageHeaderArenaNameBox>
                <span className={CyClass('cy-header-arena-title')}>
                    { props.menuItem.text }
                </span>
                {newGamesIndication}
            </HomepageHeaderArenaNameBox>
            </Link>
        </HomepageHeaderArenaTileBox>
    );
});

export const HomePageHeaderArenaMenuItemComponent = withServices(ComponentBody, ['homepage', 'location', 'mediaQuery', 'device', 'theme', 'configuration', 'translation', 'analytics']);